import type { NotificationPermissionsStatus } from 'expo-notifications'

/**
 * ATL-1345 — will the OS put up its OWN banner for a support reply while the app is open?
 *
 * The backend pushes every agent reply (`pushReplyToCustomer`), and the app's foreground handler is
 * `shouldShowAlert: true`. So on a device where push works, the merchant already sees a banner with
 * the preview, and tapping it already opens the chat. The in-app toast (ATL-1343) must stand down
 * there, or the same text is announced twice at the top of the screen.
 *
 * PM decision (ATL-1345): on native, show the toast only when push is not enabled for this user;
 * on web, always show it (there is no push on web at all). The OS banner is never suppressed —
 * the toast has far more preconditions than the banner (admin role, button mounted, kill switch,
 * a known conversation), so suppressing the banner in the toast's favour would turn a working
 * announcement into silence for a cashier or a brand-new merchant.
 *
 * Pure, and imported by type only, so it can be tested without loading the native module.
 */
export type OsSupportBanner = 'shown' | 'not-shown' | 'unknown'

// Mirror expo-notifications' `IosAuthorizationStatus.PROVISIONAL` and `IosAlertStyle.NONE`.
// Numeric on purpose: importing the runtime enums would load the native module into this file.
const IOS_AUTHORIZATION_PROVISIONAL = 3
const IOS_ALERT_STYLE_NONE = 0

export interface BannerCapability {
  /** A simulator never receives a push token, so it can never show a push banner. */
  isPhysicalDevice: boolean
  /**
   * Whether this device holds a push token. The backend can only push to a stored token, so
   * `granted` without one still produces no banner. `undefined` = not known yet (judge on
   * permission alone, which is the PM's rule).
   */
  hasPushToken?: boolean
}

/**
 * The verdict for one permission reading.
 *
 * Deliberately stricter than `status === 'granted'`, and only ever in one direction: every
 * additional check below can turn "shown" into "not-shown", never the reverse. That keeps the
 * worst case equal to the PM's rule (suppress the toast when push is granted) and can only add
 * toasts where no banner will appear — a duplicate is recoverable, a missing announcement is not.
 *
 * - iOS **provisional** authorisation is granted, but delivers quietly to Notification Center with
 *   no banner.
 * - iOS with alerts switched off (`allowsAlert === false`, or alert style None) likewise shows no
 *   banner. `allowsAlert: null` means iOS did not say, and is not read as off.
 */
export const osBannerFromPermission = (
  perms: Pick<NotificationPermissionsStatus, 'status' | 'ios'>,
  capability: BannerCapability
): Exclude<OsSupportBanner, 'unknown'> => {
  if (!capability.isPhysicalDevice) return 'not-shown'
  if (capability.hasPushToken === false) return 'not-shown'
  if (perms.status !== 'granted') return 'not-shown'
  const ios = perms.ios
  if (ios) {
    if (ios.status === IOS_AUTHORIZATION_PROVISIONAL) return 'not-shown'
    if (ios.allowsAlert === false) return 'not-shown'
    if (ios.alertStyle === IOS_ALERT_STYLE_NONE) return 'not-shown'
  }
  return 'shown'
}

/**
 * Apply a foreground re-read to the current verdict.
 *
 * A re-read can only DOWNGRADE. It sees the permission but not the token: the token is fetched by
 * the push prompt, which runs once per app start. So a merchant who switches push ON in Settings
 * still has no token (and so no banner) until the next start — upgrading here would suppress the
 * toast with nothing to replace it. Switching push OFF, on the other hand, takes the banner away
 * immediately, and the toast must take over at once.
 */
export const applyForegroundReading = (
  current: OsSupportBanner,
  reading: Exclude<OsSupportBanner, 'unknown'>
): OsSupportBanner => (reading === 'not-shown' ? 'not-shown' : current)

/**
 * Apply the push prompt's token-step verdict. It is computed from the permission reading taken
 * BEFORE the token request, which can take long enough for the merchant to switch push off in
 * Settings meanwhile — a foreground re-read has then already downgraded to `not-shown`, and this
 * older reading must not upgrade it back. The permission-step verdict has already replaced
 * `unknown`, so there is nothing for this step to upgrade legitimately.
 */
export const applyTokenVerdict = (
  current: OsSupportBanner,
  verdict: Exclude<OsSupportBanner, 'unknown'>
): Exclude<OsSupportBanner, 'unknown'> =>
  current === 'not-shown' || verdict === 'not-shown' ? 'not-shown' : 'shown'
